import React, { useRef, useState, useEffect } from 'react';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import AccessTimeIcon from '@material-ui/icons/AccessTime';

/**
 * Parse any user input format into { h, m }:
 * Supports:
 * - "5 min", "50 min", "5m", "50m", "45 mins" -> { h: 0, m: 5 }, { h: 0, m: 50 }
 * - "1 hr", "1h", "2 hrs" -> { h: 1, m: 0 }
 * - "1 hr 30 min", "1h 30m", "1h30" -> { h: 1, m: 30 }
 * - "2 hr 30 min", "02:30 min", "02:30 hr", "02:30", "2:30" -> { h: 2, m: 30 }
 * - "430" (4h 30m), "0430", "230" (2h 30m), "130" (1h 30m), "800" (8h 00m)
 * - "4" (4h 00m), "04" (4h 00m)
 * - "4.5" (4h 30m), "2.5" (2h 30m), "1.25" (1h 15m), "1.5" (1h 30m)
 * - ":50", ":05", "0:50", "0:05", "00:50", "00:05"
 */
export const parseTimelineHoursMinutes = (val) => {
  if (!val || typeof val !== 'string') return null;
  let str = val.trim().toLowerCase();
  if (!str) return null;

  // Case 1: Format like '1h 30m', '1 hr 30 min', '1 hour 30 mins', '1h30'
  const hAndMRegex = /^(\d+)\s*(?:hrs?|hours?|h)\s*(\d+)\s*(?:mins?|minutes?|m)?$/;
  const hmMatch = str.match(hAndMRegex);
  if (hmMatch) {
    const h = parseInt(hmMatch[1], 10);
    const m = parseInt(hmMatch[2], 10);
    if (!isNaN(h) && !isNaN(m)) return { h, m };
  }

  // Case 2: Only minutes, e.g. '5 min', '50 mins', '45m', '50 minute', '5m'
  const minOnlyRegex = /^(\d+)\s*(?:mins?|minutes?|m)$/;
  const mMatch = str.match(minOnlyRegex);
  if (mMatch) {
    const totalM = parseInt(mMatch[1], 10);
    if (!isNaN(totalM)) {
      return { h: Math.floor(totalM / 60), m: totalM % 60 };
    }
  }

  // Case 3: Only hours, e.g. '1 hr', '2 hrs', '1h', '2 hours'
  const hrOnlyRegex = /^(\d+)\s*(?:hrs?|hours?|h)$/;
  const hMatch = str.match(hrOnlyRegex);
  if (hMatch) {
    const h = parseInt(hMatch[1], 10);
    if (!isNaN(h)) return { h, m: 0 };
  }

  // Strip 'hr', 'hrs', 'hours', 'min', 'mins', 'minutes'
  let cleanStr = str.replace(/hours?|hrs?|h\b/g, '').replace(/minutes?|mins?|m\b/g, '').trim();

  // Decimal hours like "1.5" or "4.5" or "4.30"
  if (/^\d+(\.\d+)?$/.test(cleanStr) && cleanStr.includes('.')) {
    const parts = cleanStr.split('.');
    const h = parseInt(parts[0] || '0', 10);
    const decStr = parts[1] || '0';
    // If user typed 4.30, treat .30 as 30 minutes
    let m = 0;
    if (decStr.length === 2 && parseInt(decStr, 10) < 60) {
      m = parseInt(decStr, 10);
    } else {
      const num = parseFloat(cleanStr);
      m = Math.round((num - h) * 60);
    }
    if (isNaN(h) || isNaN(m)) return null;
    return { h, m };
  }

  // Replace dot or space with colon
  cleanStr = cleanStr.replace(/[.\s]/g, ':').replace(/:{2,}/g, ':');
  const clean = cleanStr.replace(/[^0-9:]/g, '');
  if (!clean) return null;

  if (clean.includes(':')) {
    const [hStr, mStr] = clean.split(':');
    const h = parseInt(hStr || '0', 10);
    const m = mStr !== undefined && mStr !== '' ? parseInt(mStr, 10) : 0;
    if (isNaN(h) || isNaN(m)) return null;
    return { h, m };
  }

  // Pure digits without colon:
  // 4 digits: e.g. "0430" -> 4h 30m, "0050" -> 0h 50m, "0005" -> 0h 5m
  if (clean.length === 4) {
    const h = parseInt(clean.slice(0, 2), 10);
    const m = parseInt(clean.slice(2), 10);
    if (isNaN(h) || isNaN(m)) return null;
    return { h, m };
  }

  // 3 digits: e.g. "430" -> 4h 30m, "230" -> 2h 30m, "130" -> 1h 30m, "050" -> 0h 50m, "005" -> 0h 5m
  if (clean.length === 3) {
    const h = parseInt(clean.slice(0, 1), 10);
    const m = parseInt(clean.slice(1), 10);
    if (isNaN(h) || isNaN(m)) return null;
    return { h, m };
  }

  // 1 or 2 digits: e.g. "4" -> 4 hours, "08" -> 8 hours
  if (clean.length <= 2) {
    const h = parseInt(clean, 10);
    if (isNaN(h)) return null;
    return { h, m: 0 };
  }

  return null;
};

/**
 * Validates timeline duration string.
 * Returns { isValid, error }
 */
export const validateTimeline = (val, maxHours = 8, maxMinutes = null) => {
  if (!val || typeof val !== 'string' || !val.trim()) {
    return { isValid: true, error: null };
  }

  const parsed = parseTimelineHoursMinutes(val);
  if (!parsed) {
    return { isValid: false, error: 'Add proper time line in hours' };
  }

  const { h, m } = parsed;

  // Minutes validation: 00 to 59 only
  if (m < 0 || m >= 60) {
    return { isValid: false, error: 'Add proper time line in hours' };
  }

  const limitMinutes = maxMinutes !== null && maxMinutes !== undefined
    ? maxMinutes
    : (maxHours !== null && maxHours !== undefined ? maxHours * 60 : Infinity);

  if (limitMinutes !== Infinity) {
    if (h * 60 + m > limitMinutes) {
      return {
        isValid: false,
        error: maxMinutes !== null && maxMinutes !== undefined && limitMinutes < 480
          ? 'Exceeds available timeline'
          : `Max ${maxHours || 8} hours`
      };
    }
  } else {
    if (h < 0) {
      return { isValid: false, error: 'Add proper time line in hours' };
    }
  }

  return { isValid: true, error: null };
};

export const isOverEightHours = (val) => {
  if (!val || typeof val !== 'string') return false;
  const parsed = parseTimelineHoursMinutes(val);
  if (!parsed) return false;
  return (parsed.h * 60 + parsed.m) > 480;
};

/**
 * Formats timeline duration to HH:mm string.
 */
export const clampAndFormatTimeline = (val, maxHours = 8, maxMinutes = null) => {
  if (!val || typeof val !== 'string') return '';
  const parsed = parseTimelineHoursMinutes(val);
  if (!parsed) return '';

  let { h, m } = parsed;
  if (isNaN(h) || h < 0) h = 0;
  if (isNaN(m) || m < 0) m = 0;
  if (m > 59) m = 59;

  const limitMinutes = maxMinutes !== null && maxMinutes !== undefined
    ? maxMinutes
    : (maxHours !== null && maxHours !== undefined ? maxHours * 60 : Infinity);

  if (limitMinutes !== Infinity) {
    if (h * 60 + m > limitMinutes) {
      h = Math.floor(limitMinutes / 60);
      m = limitMinutes % 60;
    }
  }

  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/**
 * Formats a total minute count to HH:mm hr format
 */
export const formatMinutesToTimeline = (totalMinutes) => {
  if (totalMinutes === null || totalMinutes === undefined || totalMinutes < 0) return '00:00 hr';
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')} hr`;
};

export const sanitizeTimelineInput = (raw, prevVal = '') => {
  if (!raw) return '';

  // Strip hr and anything not a digit or colon
  let val = String(raw).replace(/hr/gi, '').replace(/[^0-9:]/g, '');

  const isDeleting = val.length < prevVal.length;
  if (isDeleting) {
    return val;
  }

  // If user typed colon alone -> "00:"
  if (val === ':') {
    return '00:';
  }

  // If user typed 4 digits without colon (e.g. "0530" or "0800")
  if (/^\d{4}$/.test(val)) {
    let h = parseInt(val.slice(0, 2), 10);
    let m = parseInt(val.slice(2, 4), 10);
    if (h > 8 || h === 8) {
      return '08:00';
    }
    if (m > 59) m = 59;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  // If user typed 3 digits without colon (e.g. "530" or "800")
  if (/^\d{3}$/.test(val)) {
    let h = parseInt(val.slice(0, 1), 10);
    let m = parseInt(val.slice(1, 3), 10);
    if (h > 8 || h === 8) {
      return '08:00';
    }
    if (m > 59) m = 59;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  // If there's a colon:
  if (val.includes(':')) {
    let [hStr, ...mParts] = val.split(':');
    let mStr = mParts.join('');

    let h = parseInt(hStr || '0', 10);
    if (h > 8) {
      return '08:00';
    }

    let formattedH = hStr.length === 1 ? `0${hStr}` : hStr.slice(0, 2);

    // If hours is 8, minutes CANNOT be > 0
    if (h === 8) {
      if (mStr && mStr !== '0' && mStr !== '00') {
        return '08:00';
      }
      return mStr.length > 0 ? `08:${mStr.slice(0, 2)}` : '08:';
    }

    // Minutes: cannot exceed 2 digits, and first digit cannot be 6-9
    if (mStr.length >= 1) {
      let firstMinDigit = parseInt(mStr[0], 10);
      if (firstMinDigit > 5) {
        mStr = '59';
      } else if (mStr.length > 2) {
        mStr = mStr.slice(0, 2);
      }
    }

    return `${formattedH}:${mStr}`;
  }

  // Pure digits without colon:
  if (val.length === 1) {
    let d = parseInt(val, 10);
    if (d > 8) {
      return '08:00';
    }
    return val;
  }

  if (val.length === 2) {
    if (val.startsWith('0')) {
      let d = parseInt(val[1], 10);
      if (d > 8) {
        return '08:00';
      }
      return `${val}:`;
    }

    let h = parseInt(val[0], 10);
    let m1 = parseInt(val[1], 10);

    if (h > 8 || h === 8) {
      return '08:00';
    }

    if (m1 > 5) {
      m1 = 5;
    }

    return `0${h}:${m1}`;
  }

  return val.slice(0, 5);
};

const TimelineInputField = ({
  value = '',
  onChange,
  onBlur,
  onFocus,
  label = 'Add time line',
  id = 'task_timeline',
  name = 'timeline',
  error = false,
  helperText = '',
  disabled = false,
  required = false,
  fullWidth = true,
  className = '',
  size = 'medium',
  maxHours = 8,
  maxMinutes = null,
  helperTextColor = null,
  onMaxExceeded,
  ...otherProps
}) => {
  const parsed = parseTimelineHoursMinutes(value);
  const initialH = parsed ? String(parsed.h).padStart(2, '0') : '';
  const initialM = parsed ? String(parsed.m).padStart(2, '0') : '';

  const [hours, setHours] = useState(initialH);
  const [minutes, setMinutes] = useState(initialM);
  const [isFocused, setIsFocused] = useState(false);

  const hoursRef = useRef(null);
  const minutesRef = useRef(null);
  const colonRef = useRef(null);
  const lastExceededWarnRef = useRef(0);

  const triggerMaxExceeded = () => {
    const now = Date.now();
    if (onMaxExceeded && now - lastExceededWarnRef.current > 1500) {
      lastExceededWarnRef.current = now;
      onMaxExceeded();
    }
  };

  const effectiveMaxMinutes = maxMinutes !== null && maxMinutes !== undefined
    ? maxMinutes
    : (maxHours !== null && maxHours !== undefined ? maxHours * 60 : Infinity);

  const effectiveMaxHours = Math.min(
    effectiveMaxMinutes !== Infinity ? Math.floor(effectiveMaxMinutes / 60) : 99,
    maxHours !== null && maxHours !== undefined && maxHours > 0 ? maxHours : 99
  );

  useEffect(() => {
    if (!isFocused) {
      const p = parseTimelineHoursMinutes(value);
      setHours(p ? String(p.h).padStart(2, '0') : '');
      setMinutes(p ? String(p.m).padStart(2, '0') : '');
    }
  }, [value, isFocused]);

  const commitValue = (hVal, mVal) => {
    let cleanH = (hVal !== undefined ? hVal : (hoursRef.current ? hoursRef.current.value : hours) || '').trim();
    let cleanM = (mVal !== undefined ? mVal : (minutesRef.current ? minutesRef.current.value : minutes) || '').trim();

    if (!cleanH && !cleanM) {
      if (onChange) onChange('');
      return;
    }

    let h = parseInt(cleanH || '0', 10);
    let m = parseInt(cleanM || '0', 10);
    if (isNaN(h) || h < 0) h = 0;
    if (isNaN(m) || m < 0) m = 0;
    if (m > 59) m = 59;

    if (effectiveMaxMinutes !== Infinity) {
      if (h * 60 + m > effectiveMaxMinutes) {
        h = Math.floor(effectiveMaxMinutes / 60);
        m = effectiveMaxMinutes % 60;
      }
    }

    const formattedH = String(h).padStart(2, '0');
    const formattedM = String(m).padStart(2, '0');
    setHours(formattedH);
    setMinutes(formattedM);
    if (hoursRef.current) hoursRef.current.value = formattedH;
    if (minutesRef.current) minutesRef.current.value = formattedM;

    const result = `${formattedH}:${formattedM}`;
    if (onChange) {
      onChange(result);
    }
  };

  const handlePaste = (e) => {
    const text = (e.clipboardData ? e.clipboardData.getData('text') : '').trim();
    if (!text) return;
    const p = parseTimelineHoursMinutes(text);
    if (p) {
      e.preventDefault();
      let h = p.h;
      let m = p.m > 59 ? 59 : p.m;
      let exceeded = false;
      if (effectiveMaxMinutes !== Infinity) {
        if (h * 60 + m > effectiveMaxMinutes) {
          h = Math.floor(effectiveMaxMinutes / 60);
          m = effectiveMaxMinutes % 60;
          exceeded = true;
        }
      }
      if (exceeded) triggerMaxExceeded();
      const formattedH = String(h).padStart(2, '0');
      const formattedM = String(m).padStart(2, '0');
      setHours(formattedH);
      setMinutes(formattedM);
      if (hoursRef.current) hoursRef.current.value = formattedH;
      if (minutesRef.current) minutesRef.current.value = formattedM;
      commitValue(formattedH, formattedM);
      setTimeout(() => {
        minutesRef.current?.focus();
        minutesRef.current?.select();
      }, 0);
    }
  };

  const handleHoursChange = (e) => {
    const rawVal = e.target.value;
    const raw = rawVal.replace(/[^0-9]/g, '').slice(0, 2);
    const currentM = (minutesRef.current ? minutesRef.current.value : minutes) || '00';

    if (!raw) {
      setHours('');
      if (hoursRef.current) hoursRef.current.value = '';
      if (onChange) onChange(`00:${currentM.padStart(2, '0')}`);
      return;
    }

    if (effectiveMaxHours === 0) {
      setHours('00');
      if (hoursRef.current) hoursRef.current.value = '00';
      minutesRef.current?.focus();
      minutesRef.current?.select();
      return;
    }

    if (raw.length === 1) {
      setHours(raw);
      if (hoursRef.current) hoursRef.current.value = raw;
      if (onChange) onChange(`${raw.padStart(2, '0')}:${currentM.padStart(2, '0')}`);
      return;
    }

    // Exactly 2 digits typed (e.g. "10", "02", "01", "08", "24")
    let num = parseInt(raw, 10);
    let exceeded = false;
    if (num > effectiveMaxHours) {
      num = effectiveMaxHours;
      exceeded = true;
    }
    const formatted = String(num).padStart(2, '0');
    setHours(formatted);
    if (hoursRef.current) hoursRef.current.value = formatted;

    let newM = currentM;
    if (effectiveMaxMinutes !== Infinity) {
      const curTotal = num * 60 + parseInt(currentM || '0', 10);
      if (curTotal > effectiveMaxMinutes) {
        newM = String(effectiveMaxMinutes % 60).padStart(2, '0');
        setMinutes(newM);
        if (minutesRef.current) minutesRef.current.value = newM;
      }
    }

    if (exceeded) {
      triggerMaxExceeded();
    }

    if (onChange) onChange(`${formatted}:${newM.padStart(2, '0')}`);

    // Auto-advance to minutes
    minutesRef.current?.focus();
    minutesRef.current?.select();
  };

  const handleMinutesChange = (e) => {
    const rawVal = e.target.value;
    const raw = rawVal.replace(/[^0-9]/g, '').slice(0, 2);
    const currentH = parseInt((hoursRef.current ? hoursRef.current.value : hours) || '0', 10);

    let maxAllowedM = 59;
    if (effectiveMaxMinutes !== Infinity) {
      const remainingM = effectiveMaxMinutes - (currentH * 60);
      if (remainingM <= 0) {
        maxAllowedM = 0;
      } else if (remainingM < 59) {
        maxAllowedM = remainingM;
      }
    }

    if (maxAllowedM === 0) {
      setMinutes('00');
      if (minutesRef.current) minutesRef.current.value = '00';
      if (onChange) onChange(`${String(currentH).padStart(2, '0')}:00`);
      if (raw && parseInt(raw, 10) > 0) {
        triggerMaxExceeded();
      }
      return;
    }

    if (!raw) {
      setMinutes('');
      if (minutesRef.current) minutesRef.current.value = '';
      if (onChange) onChange(`${String(currentH).padStart(2, '0')}:00`);
      return;
    }

    if (raw.length === 1) {
      let mDigit = parseInt(raw, 10);
      let exceeded = false;
      if (mDigit > maxAllowedM) {
        mDigit = maxAllowedM;
        exceeded = true;
      }
      setMinutes(String(mDigit));
      if (minutesRef.current) minutesRef.current.value = String(mDigit);
      if (onChange) onChange(`${String(currentH).padStart(2, '0')}:${String(mDigit).padStart(2, '0')}`);
      if (exceeded) {
        triggerMaxExceeded();
      }
      return;
    }

    let num = parseInt(raw, 10);
    let exceeded = false;
    if (num > maxAllowedM) {
      num = maxAllowedM;
      exceeded = true;
    }
    const formattedM = String(num).padStart(2, '0');
    setMinutes(formattedM);
    if (minutesRef.current) minutesRef.current.value = formattedM;
    if (onChange) onChange(`${String(currentH).padStart(2, '0')}:${formattedM}`);
    if (exceeded) {
      triggerMaxExceeded();
    }
  };

  const handleHoursKeyDown = (e) => {
    if (e.key === 'Enter') {
      commitValue();
      return;
    }
    if (e.key === ':' || e.key === 'Tab' || e.key === 'ArrowRight' || e.key === ' ') {
      if (e.key !== 'Tab') e.preventDefault();
      const curH = (hoursRef.current ? hoursRef.current.value : hours) || '';
      const formattedH = curH ? curH.padStart(2, '0') : '00';
      setHours(formattedH);
      if (hoursRef.current) hoursRef.current.value = formattedH;
      if (onChange) onChange(`${formattedH}:${(minutes || '00').padStart(2, '0')}`);
      minutesRef.current?.focus();
      minutesRef.current?.select();
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      let exceeded = false;
      let h = parseInt((hoursRef.current ? hoursRef.current.value : hours) || '0', 10) + 1;
      if (h > effectiveMaxHours) {
        h = effectiveMaxHours;
        exceeded = true;
      }
      const formatted = String(h).padStart(2, '0');
      setHours(formatted);
      if (hoursRef.current) hoursRef.current.value = formatted;
      let m = minutes;
      if (effectiveMaxMinutes !== Infinity) {
        if (h * 60 + parseInt(m || '0', 10) > effectiveMaxMinutes) {
          m = String(effectiveMaxMinutes % 60).padStart(2, '0');
          setMinutes(m);
        }
      }
      if (exceeded) {
        triggerMaxExceeded();
      }
      commitValue(formatted, m);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      let h = parseInt((hoursRef.current ? hoursRef.current.value : hours) || '0', 10) - 1;
      if (h < 0) h = 0;
      const formatted = String(h).padStart(2, '0');
      setHours(formatted);
      if (hoursRef.current) hoursRef.current.value = formatted;
      commitValue(formatted, minutes);
      return;
    }
  };

  const handleMinutesKeyDown = (e) => {
    if (e.key === 'Enter') {
      commitValue();
      return;
    }
    if (e.key === ' ' || e.key === ':') {
      e.preventDefault();
      return;
    }
    if (e.key === 'Backspace' && (minutes === '' || e.target.selectionStart === 0)) {
      if (effectiveMaxHours > 0) {
        hoursRef.current?.focus();
      }
      return;
    }
    if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) {
      if (effectiveMaxHours > 0) {
        e.preventDefault();
        hoursRef.current?.focus();
        hoursRef.current?.select();
      }
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const currentH = parseInt((hoursRef.current ? hoursRef.current.value : hours) || '0', 10);
      let maxAllowedM = 59;
      if (effectiveMaxMinutes !== Infinity) {
        const remainingM = effectiveMaxMinutes - (currentH * 60);
        maxAllowedM = remainingM <= 0 ? 0 : Math.min(59, remainingM);
      }
      if (maxAllowedM === 0) {
        setMinutes('00');
        if (minutesRef.current) minutesRef.current.value = '00';
        return;
      }
      let m = parseInt((minutesRef.current ? minutesRef.current.value : minutes) || '0', 10) + 15;
      if (m > maxAllowedM) m = 0;
      const formattedM = String(m).padStart(2, '0');
      setMinutes(formattedM);
      if (minutesRef.current) minutesRef.current.value = formattedM;
      commitValue(hours, formattedM);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const currentH = parseInt((hoursRef.current ? hoursRef.current.value : hours) || '0', 10);
      let maxAllowedM = 59;
      if (effectiveMaxMinutes !== Infinity) {
        const remainingM = effectiveMaxMinutes - (currentH * 60);
        maxAllowedM = remainingM <= 0 ? 0 : Math.min(59, remainingM);
      }
      if (maxAllowedM === 0) {
        setMinutes('00');
        if (minutesRef.current) minutesRef.current.value = '00';
        return;
      }
      let m = parseInt((minutesRef.current ? minutesRef.current.value : minutes) || '0', 10) - 15;
      if (m < 0) m = Math.floor(maxAllowedM / 15) * 15;
      const formattedM = String(m).padStart(2, '0');
      setMinutes(formattedM);
      if (minutesRef.current) minutesRef.current.value = formattedM;
      commitValue(hours, formattedM);
      return;
    }
  };

  const isSmall = size === 'small';
  const validation = validateTimeline(`${hours || '00'}:${minutes || '00'}`, maxHours, maxMinutes);
  const showError = Boolean(error || !validation.isValid);
  const defaultHelper = (maxHours === 8 && (maxMinutes === null || maxMinutes >= 480)) ? 'Max 8 hours' : '';
  const displayHelperText = !validation.isValid
    ? validation.error
    : (helperText !== undefined && helperText !== '' ? helperText : (helperText === null || helperText === false ? '' : defaultHelper));

  const handleHoursBlur = (e) => {
    if (e.relatedTarget === minutesRef.current) {
      return;
    }
    setIsFocused(false);
    commitValue();
    if (onBlur) onBlur(e);
  };

  const handleMinutesBlur = (e) => {
    if (e.relatedTarget === hoursRef.current) {
      return;
    }
    setIsFocused(false);
    commitValue();
    if (onBlur) onBlur(e);
  };

  return (
    <TextField
      id={id}
      name={name}
      fullWidth={fullWidth}
      variant="outlined"
      size={size}
      label={label}
      required={required}
      error={Boolean(showError)}
      helperText={displayHelperText}
      FormHelperTextProps={{
        ...(otherProps.FormHelperTextProps || {}),
        style: {
          whiteSpace: 'nowrap',
          ...(helperTextColor ? { color: helperTextColor, opacity: 1 } : {}),
          ...((otherProps.FormHelperTextProps && otherProps.FormHelperTextProps.style) || {})
        }
      }}
      disabled={disabled}
      className={`tm-timeline-textfield ${className}`}
      onClick={(e) => {
        if (!disabled) {
          if (effectiveMaxHours === 0 && minutesRef.current) {
            minutesRef.current.focus();
            minutesRef.current.select();
          } else if (colonRef.current && e.clientX > colonRef.current.getBoundingClientRect().right && minutesRef.current) {
            minutesRef.current.focus();
            minutesRef.current.select();
          } else if (hoursRef.current) {
            hoursRef.current.focus();
            hoursRef.current.select();
          }
        }
      }}
      InputLabelProps={{
        shrink: true,
        className: 'tm-timeline-label'
      }}
      InputProps={{
        style: {
          height: isSmall ? '40px' : '56px',
          minHeight: isSmall ? '40px' : '56px',
          padding: isSmall ? '0 10px' : '0 14px',
          cursor: disabled ? 'not-allowed' : 'text'
        },
        startAdornment: (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              fontSize: isSmall ? '14px' : '1rem',
              fontFamily: 'inherit',
              lineHeight: 1
            }}
          >
            <input
              ref={hoursRef}
              type="text"
              inputMode="numeric"
              placeholder="00"
              value={hours}
              maxLength={2}
              disabled={disabled}
              className="tm-time-digit-input"
              style={isSmall ? { fontSize: '14px', width: '24px' } : { width: '28px' }}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => {
                setIsFocused(true);
                const target = e.target;
                setTimeout(() => {
                  try { target.select(); } catch (_) {}
                }, 0);
                if (onFocus) onFocus();
              }}
              onBlur={handleHoursBlur}
              onChange={handleHoursChange}
              onKeyDown={handleHoursKeyDown}
              onPaste={handlePaste}
            />
            <span
              ref={colonRef}
              className="tm-time-colon"
              style={isSmall ? { fontSize: '14px', padding: '0 2px' } : { padding: '0 3px' }}
            >
              :
            </span>
            <input
              ref={minutesRef}
              type="text"
              inputMode="numeric"
              placeholder="00"
              value={minutes}
              maxLength={2}
              disabled={disabled}
              className="tm-time-digit-input"
              style={isSmall ? { fontSize: '14px', width: '24px' } : { width: '28px' }}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => {
                setIsFocused(true);
                const target = e.target;
                setTimeout(() => {
                  try { target.select(); } catch (_) {}
                }, 0);
                if (onFocus) onFocus();
              }}
              onBlur={handleMinutesBlur}
              onChange={handleMinutesChange}
              onKeyDown={handleMinutesKeyDown}
              onPaste={handlePaste}
            />
            <span
              className="tm-timeline-hr-label"
              style={isSmall ? { fontSize: '14px', fontWeight: 600, marginLeft: '5px' } : { fontSize: '16px', fontWeight: 600, marginLeft: '6px' }}
            >
              hr
            </span>
          </div>
        ),
        endAdornment: !isSmall ? (
          <InputAdornment position="end" style={{ marginLeft: 'auto' }}>
            <AccessTimeIcon
              onClick={(e) => {
                e.stopPropagation();
                if (hoursRef.current && !disabled) {
                  hoursRef.current.focus();
                  hoursRef.current.select();
                }
              }}
              style={{
                fontSize: 20,
                color: isFocused ? '#036dda' : '#94a3b8',
                cursor: disabled ? 'not-allowed' : 'pointer'
              }}
            />
          </InputAdornment>
        ) : null,
        inputProps: {
          readOnly: true,
          tabIndex: -1,
          style: {
            display: 'none'
          }
        }
      }}
      {...otherProps}
    />
  );
};

/**
 * Segmented Timeline Input for Table Cells & Detail Cards:
 * Has separate inputs for Hours [00] and Minutes [00] separated by [:] and [hr].
 * Allows clicking directly on the front [00] to change hours, or back [00] to change minutes!
 */
export const TimelineSegmentedCell = ({
  value = '',
  onSave,
  disabled = false,
  className = '',
  style = {},
  title,
  maxHours = 99,
  minMinutes = 0,
  maxMinutes = 0,
  onMinViolated,
  onMaxViolated
}) => {
  const parsed = parseTimelineHoursMinutes(value);
  const initialH = parsed ? String(parsed.h).padStart(2, '0') : '';
  const initialM = parsed ? String(parsed.m).padStart(2, '0') : '';

  const [hours, setHours] = useState(initialH);
  const [minutes, setMinutes] = useState(initialM);
  const [isFocused, setIsFocused] = useState(false);

  const hoursRef = useRef(null);
  const minutesRef = useRef(null);
  const containerRef = useRef(null);

  const effectiveMaxMinutes = Math.min(
    maxMinutes > 0 ? maxMinutes : Infinity,
    maxHours > 0 ? maxHours * 60 : Infinity
  );

  // Sync state if external value changes while not actively focused
  React.useEffect(() => {
    if (!isFocused) {
      const p = parseTimelineHoursMinutes(value);
      setHours(p ? String(p.h).padStart(2, '0') : '');
      setMinutes(p ? String(p.m).padStart(2, '0') : '');
    }
  }, [value, isFocused]);

  const commitSave = (hVal, mVal) => {
    if (disabled) return;
    const cleanH = (hVal !== undefined ? hVal : hours || '').trim();
    const cleanM = (mVal !== undefined ? mVal : minutes || '').trim();

    if (!cleanH && !cleanM) {
      if (minMinutes > 0) {
        if (onMinViolated) {
          onMinViolated(minMinutes);
        }
        const p = parseTimelineHoursMinutes(value);
        setHours(p ? String(p.h).padStart(2, '0') : '');
        setMinutes(p ? String(p.m).padStart(2, '0') : '');
        return;
      }
      if (value) {
        onSave && onSave('');
      }
      return;
    }

    let h = parseInt(cleanH || '0', 10);
    let m = parseInt(cleanM || '0', 10);
    if (isNaN(h) || h < 0) h = 0;
    if (isNaN(m) || m < 0) m = 0;
    if (m > 59) m = 59;
    if (h > maxHours) {
      h = maxHours;
      m = 0;
    }

    const totalMins = h * 60 + m;
    if (minMinutes > 0 && totalMins < minMinutes) {
      if (onMinViolated) {
        onMinViolated(minMinutes);
      }
      const p = parseTimelineHoursMinutes(value);
      setHours(p ? String(p.h).padStart(2, '0') : '');
      setMinutes(p ? String(p.m).padStart(2, '0') : '');
      return;
    }

    if (effectiveMaxMinutes !== Infinity && totalMins > effectiveMaxMinutes) {
      if (onMaxViolated) {
        onMaxViolated(effectiveMaxMinutes);
      }
      const p = parseTimelineHoursMinutes(value);
      setHours(p ? String(p.h).padStart(2, '0') : '');
      setMinutes(p ? String(p.m).padStart(2, '0') : '');
      return;
    }

    const formattedH = String(h).padStart(2, '0');
    const formattedM = String(m).padStart(2, '0');
    setHours(formattedH);
    setMinutes(formattedM);

    const finalStr = `${formattedH}:${formattedM} hr`;
    if (finalStr !== value) {
      onSave && onSave(finalStr);
    }
  };

  const handleContainerBlur = (e) => {
    // If focus moves to another element inside the same container, do not commit yet
    if (containerRef.current && containerRef.current.contains(e.relatedTarget)) {
      return;
    }
    setIsFocused(false);
    commitSave();
  };

  const handlePaste = (e) => {
    if (disabled) return;
    const text = (e.clipboardData ? e.clipboardData.getData('text') : '').trim();
    if (!text) return;
    const p = parseTimelineHoursMinutes(text);
    if (p) {
      e.preventDefault();
      let h = p.h;
      let m = p.m > 59 ? 59 : p.m;
      if (h > maxHours) {
        h = maxHours;
        m = 0;
      }
      const totalM = h * 60 + m;
      if (minMinutes > 0 && totalM < minMinutes) {
        if (onMinViolated) onMinViolated(minMinutes);
        return;
      }
      if (effectiveMaxMinutes !== Infinity && totalM > effectiveMaxMinutes) {
        if (onMaxViolated) onMaxViolated(effectiveMaxMinutes);
        return;
      }
      const fh = String(h).padStart(2, '0');
      const fm = String(m).padStart(2, '0');
      setHours(fh);
      setMinutes(fm);
      commitSave(fh, fm);
    }
  };

  const handleHoursChange = (e) => {
    if (disabled) return;
    const rawVal = e.target.value;

    // Check if input has colon or dot (e.g. user typed "4:30" or "4.5" into hours)
    if (rawVal.includes(':') || rawVal.includes('.')) {
      const parsedFull = parseTimelineHoursMinutes(rawVal);
      if (parsedFull) {
        const fullMins = parsedFull.h * 60 + parsedFull.m;
        if (minMinutes > 0 && fullMins < minMinutes) {
          if (onMinViolated) onMinViolated(minMinutes);
          const p = parseTimelineHoursMinutes(value);
          setHours(p ? String(p.h).padStart(2, '0') : '');
          setMinutes(p ? String(p.m).padStart(2, '0') : '');
          return;
        }
        if (effectiveMaxMinutes !== Infinity && fullMins > effectiveMaxMinutes) {
          if (onMaxViolated) onMaxViolated(effectiveMaxMinutes);
          const p = parseTimelineHoursMinutes(value);
          setHours(p ? String(p.h).padStart(2, '0') : '');
          setMinutes(p ? String(p.m).padStart(2, '0') : '');
          return;
        }
        const fh = String(parsedFull.h).padStart(2, '0');
        const fm = String(parsedFull.m).padStart(2, '0');
        setHours(fh);
        setMinutes(fm);
        commitSave(fh, fm);
        return;
      }
    }

    const raw = rawVal.replace(/[^0-9]/g, '');
    if (!raw) {
      setHours('');
      return;
    }

    // If 3 or 4 digits: e.g. "430" (4h 30m), "0430", "1430"
    if (raw.length === 3 || raw.length === 4) {
      let parsedH = 0;
      let parsedM = 0;
      if (raw.length === 3) {
        parsedH = parseInt(raw[0], 10);
        parsedM = parseInt(raw.slice(1), 10);
      } else {
        parsedH = parseInt(raw.slice(0, 2), 10);
        parsedM = parseInt(raw.slice(2, 4), 10);
      }
      if (parsedM > 59) parsedM = 59;
      const totalM = parsedH * 60 + parsedM;

      if (minMinutes > 0 && totalM < minMinutes) {
        if (onMinViolated) onMinViolated(minMinutes);
        const p = parseTimelineHoursMinutes(value);
        setHours(p ? String(p.h).padStart(2, '0') : '');
        setMinutes(p ? String(p.m).padStart(2, '0') : '');
        hoursRef.current?.focus();
        hoursRef.current?.select();
        return;
      }

      if (effectiveMaxMinutes !== Infinity && totalM > effectiveMaxMinutes) {
        if (onMaxViolated) onMaxViolated(effectiveMaxMinutes);
        const p = parseTimelineHoursMinutes(value);
        setHours(p ? String(p.h).padStart(2, '0') : '');
        setMinutes(p ? String(p.m).padStart(2, '0') : '');
        hoursRef.current?.focus();
        hoursRef.current?.select();
        return;
      }

      const fh = String(parsedH).padStart(2, '0');
      const fm = String(parsedM).padStart(2, '0');
      setHours(fh);
      setMinutes(fm);
      minutesRef.current?.focus();
      minutesRef.current?.select();
      return;
    }

    if (raw.length === 1) {
      let num = parseInt(raw, 10);
      if (effectiveMaxMinutes <= 480) {
        if (num * 60 > effectiveMaxMinutes) {
          if (onMaxViolated) onMaxViolated(effectiveMaxMinutes);
          const p = parseTimelineHoursMinutes(value);
          setHours(p ? String(p.h).padStart(2, '0') : '');
          setMinutes(p ? String(p.m).padStart(2, '0') : '');
          return;
        }
        if (num === 8) {
          setHours('08');
          setMinutes('00');
          minutesRef.current?.focus();
          minutesRef.current?.select();
          return;
        }
      }
      setHours(raw);
      return;
    }

    if (raw.length >= 2) {
      let num = parseInt(raw.slice(0, 2), 10);
      if (num > maxHours) num = maxHours;

      // Check minMinutes constraint immediately while typing
      if (minMinutes > 0 && (num * 60 + 59) < minMinutes) {
        if (onMinViolated) {
          onMinViolated(minMinutes);
        }
        const p = parseTimelineHoursMinutes(value);
        setHours(p ? String(p.h).padStart(2, '0') : '');
        setMinutes(p ? String(p.m).padStart(2, '0') : '');
        hoursRef.current?.focus();
        hoursRef.current?.select();
        return;
      }

      // Check max constraint immediately while typing
      if (effectiveMaxMinutes !== Infinity && (num * 60) > effectiveMaxMinutes) {
        if (onMaxViolated) {
          onMaxViolated(effectiveMaxMinutes);
        }
        const p = parseTimelineHoursMinutes(value);
        setHours(p ? String(p.h).padStart(2, '0') : '');
        setMinutes(p ? String(p.m).padStart(2, '0') : '');
        hoursRef.current?.focus();
        hoursRef.current?.select();
        return;
      }

      const formatted = String(num).padStart(2, '0');
      setHours(formatted);
      if (num === 8 && effectiveMaxMinutes <= 480) {
        setMinutes('00');
      }
      // Auto advance to minutes
      minutesRef.current?.focus();
      minutesRef.current?.select();
    }
  };

  const handleMinutesChange = (e) => {
    if (disabled) return;
    const raw = e.target.value.replace(/[^0-9]/g, '');
    if (effectiveMaxMinutes <= 480 && parseInt(hours || '0', 10) >= 8) {
      setMinutes('00');
      return;
    }
    if (!raw) {
      setMinutes('');
      return;
    }
    if (raw.length === 1) {
      setMinutes(raw);
      return;
    }
    let num = parseInt(raw.slice(0, 2), 10);
    if (num > 59) num = 59;

    const currentH = parseInt(hours || '0', 10);
    const totalMins = currentH * 60 + num;
    if (minMinutes > 0 && totalMins < minMinutes) {
      if (onMinViolated) {
        onMinViolated(minMinutes);
      }
      const p = parseTimelineHoursMinutes(value);
      setMinutes(p ? String(p.m).padStart(2, '0') : '');
      minutesRef.current?.focus();
      minutesRef.current?.select();
      return;
    }

    if (effectiveMaxMinutes !== Infinity && totalMins > effectiveMaxMinutes) {
      if (onMaxViolated) {
        onMaxViolated(effectiveMaxMinutes);
      }
      const p = parseTimelineHoursMinutes(value);
      setMinutes(p ? String(p.m).padStart(2, '0') : '');
      minutesRef.current?.focus();
      minutesRef.current?.select();
      return;
    }

    setMinutes(String(num).padStart(2, '0'));
  };

  const handleHoursKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'Enter') {
      e.target.blur();
      commitSave();
      return;
    }
    if (e.key === ':' || e.key === ';' || e.key === 'Tab' || e.key === 'ArrowRight') {
      if (e.key !== 'Tab') e.preventDefault();
      if (hours && hours.length === 1) {
        const num = parseInt(hours, 10);
        if (minMinutes > 0 && (num * 60 + 59) < minMinutes) {
          if (onMinViolated) onMinViolated(minMinutes);
          const p = parseTimelineHoursMinutes(value);
          setHours(p ? String(p.h).padStart(2, '0') : '');
          setMinutes(p ? String(p.m).padStart(2, '0') : '');
          return;
        }
        if (effectiveMaxMinutes !== Infinity && (num * 60) > effectiveMaxMinutes) {
          if (onMaxViolated) onMaxViolated(effectiveMaxMinutes);
          const p = parseTimelineHoursMinutes(value);
          setHours(p ? String(p.h).padStart(2, '0') : '');
          setMinutes(p ? String(p.m).padStart(2, '0') : '');
          return;
        }
        setHours(String(num).padStart(2, '0'));
      }
      minutesRef.current?.focus();
      minutesRef.current?.select();
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      let h = parseInt(hours || '0', 10) + 1;
      if (h > maxHours) h = maxHours;
      const currentM = parseInt(minutes || '0', 10);
      if (effectiveMaxMinutes !== Infinity && (h * 60 + currentM) > effectiveMaxMinutes) {
        if (onMaxViolated) onMaxViolated(effectiveMaxMinutes);
        return;
      }
      const formatted = String(h).padStart(2, '0');
      setHours(formatted);
      if (effectiveMaxMinutes <= 480 && h === 8) setMinutes('00');
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      let h = parseInt(hours || '0', 10) - 1;
      if (h < 0) h = 0;
      const currentM = parseInt(minutes || '0', 10);
      if (minMinutes > 0 && (h * 60 + currentM) < minMinutes) {
        if (onMinViolated) {
          onMinViolated(minMinutes);
        }
        return;
      }
      setHours(String(h).padStart(2, '0'));
      return;
    }
  };

  const handleMinutesKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'Enter') {
      e.target.blur();
      commitSave();
      return;
    }
    if (e.key === 'Backspace' && (minutes === '' || e.target.selectionStart === 0)) {
      hoursRef.current?.focus();
      hoursRef.current?.select();
      return;
    }
    if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) {
      e.preventDefault();
      hoursRef.current?.focus();
      hoursRef.current?.select();
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (effectiveMaxMinutes <= 480 && parseInt(hours || '0', 10) >= 8) {
        setMinutes('00');
        return;
      }
      let m = parseInt(minutes || '0', 10) + 15;
      if (m > 59) m = 0;
      const currentH = parseInt(hours || '0', 10);
      if (effectiveMaxMinutes !== Infinity && (currentH * 60 + m) > effectiveMaxMinutes) {
        if (onMaxViolated) onMaxViolated(effectiveMaxMinutes);
        return;
      }
      setMinutes(String(m).padStart(2, '0'));
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (effectiveMaxMinutes <= 480 && parseInt(hours || '0', 10) >= 8) {
        setMinutes('00');
        return;
      }
      let m = parseInt(minutes || '0', 10) - 15;
      if (m < 0) m = 45;
      const currentH = parseInt(hours || '0', 10);
      if (minMinutes > 0 && (currentH * 60 + m) < minMinutes) {
        if (onMinViolated) {
          onMinViolated(minMinutes);
        }
        return;
      }
      setMinutes(String(m).padStart(2, '0'));
      return;
    }
  };

  const hasValue = (parseInt(hours || '0', 10) > 0 || parseInt(minutes || '0', 10) > 0);
  const tooltipText = title !== undefined ? title : (hasValue ? 'Edit timeline' : 'Add timeline');

  return (
    <div
      ref={containerRef}
      className={`tm-timeline-cell-badge ${isFocused ? 'is-focused' : ''} ${disabled ? 'is-disabled' : ''} ${className}`}
      style={{
        ...(disabled ? { cursor: 'default' } : {}),
        ...style
      }}
      onBlur={handleContainerBlur}
      onMouseDown={(e) => {
        if (disabled) return;
        if (!isFocused) {
          e.preventDefault();
          hoursRef.current?.focus();
          hoursRef.current?.select();
        }
      }}
      onClick={(e) => {
        if (disabled) return;
        if (!isFocused) {
          hoursRef.current?.focus();
          hoursRef.current?.select();
        }
      }}
      title={tooltipText}
    >
      <input
        ref={hoursRef}
        type="text"
        inputMode="numeric"
        className="tm-timeline-seg-input tm-timeline-hours"
        placeholder="00"
        maxLength={4}
        disabled={disabled}
        readOnly={disabled}
        value={hours}
        onChange={handleHoursChange}
        onPaste={handlePaste}
        onFocus={(e) => {
          if (disabled) return;
          setIsFocused(true);
          e.target.select();
        }}
        onKeyDown={handleHoursKeyDown}
      />
      <span className="tm-timeline-sep">:</span>
      <input
        ref={minutesRef}
        type="text"
        inputMode="numeric"
        className="tm-timeline-seg-input tm-timeline-minutes"
        placeholder="00"
        maxLength={2}
        disabled={disabled}
        readOnly={disabled}
        value={minutes}
        onChange={handleMinutesChange}
        onPaste={handlePaste}
        onFocus={(e) => {
          if (disabled) return;
          setIsFocused(true);
          e.target.select();
        }}
        onKeyDown={handleMinutesKeyDown}
      />
      <span className="tm-timeline-unit">hr</span>
    </div>
  );
};

export default TimelineInputField;
